import {
  useState, useEffect, useRef, useMemo,
} from 'react';
import { useVirtualizer } from '@tanstack/react-virtual';
import {
  Checkbox, LoadingOverlay, Popover, UnstyledButton,
} from '@mantine/core';
import { IconFilter } from '@tabler/icons-react';
import PropTypes from 'prop-types';
import ColumnContent from '@/components/Table/ColumnContent';
import ColumnFilter from '@/components/Table/ColumnFilter';
import NoData from '@/components/Chart/noData';
import { filterAndSortData } from '@/components/Table/Table.functions';
import styles from './style.module.css';

const ROW_HEIGHT = 45;
const OVERSCAN = 5;

const VirtualTable = (props) => {
  const {
    columns = [],
    getData = () => {},
    filterOptions = {},
    columnFilters = {},
    setColumnFilters = () => {},
    onSelectRow = () => {},
    showSelectionColumn = false,
    height = '60vh',
    showEmptyState = true,
    ready = false,
  } = props;

  const [allRows, setAllRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selection, setSelection] = useState([]);
  const scrollRef = useRef(null);
  const hasFetchedRef = useRef(false);

  // Fetch all data once on mount
  useEffect(() => {
    let cancelled = false;
    const fetchData = async () => {
      setLoading(true);
      try {
        const result = await getData(0, 0, {}, {});
        if (!cancelled) {
          if (result && Array.isArray(result.data)) {
            hasFetchedRef.current = true;
            setAllRows(result.data);
            setLoading(false);
          }
          // If result is null the fetch was blocked (StrictMode duplicate guard) —
          // keep loading=true so the spinner stays visible until the retry completes.
        }
      } catch {
        if (!cancelled) setLoading(false);
      }
    };
    fetchData();
    return () => { cancelled = true; };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Retry if initial fetch was blocked by duplicate-fetch guard (React StrictMode double-mount).
  // When ready=true the parent has finished fetching — retry to pull from cache
  // (even if data is empty).
  useEffect(() => {
    if (hasFetchedRef.current || !ready) return;
    let cancelled = false;
    const retry = async () => {
      try {
        const result = await getData(0, 0, {}, {});
        if (!cancelled) {
          if (result && Array.isArray(result.data)) {
            hasFetchedRef.current = true;
            setAllRows(result.data);
          }
          setLoading(false);
        }
      } catch {
        if (!cancelled) setLoading(false);
      }
    };
    retry();
    // eslint-disable-next-line consistent-return
    return () => { cancelled = true; };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ready]);

  // Derive filtered rows from allRows + columnFilters (client-side only)
  const filteredRows = useMemo(
    () => filterAndSortData(allRows, columnFilters),
    [allRows, columnFilters],
  );

  // Reset selection when filters change (use stringify to avoid reference instability)
  useEffect(() => {
    if (selection.length > 0) setSelection([]);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [JSON.stringify(columnFilters)]);

  // Keep ref to latest onSelectRow to avoid stale closure
  const onSelectRowRef = useRef(onSelectRow);
  useEffect(() => { onSelectRowRef.current = onSelectRow; });

  // Notify parent whenever selection changes
  useEffect(() => {
    onSelectRowRef.current(selection);
  }, [selection]);

  const virtualizer = useVirtualizer({
    count: filteredRows.length,
    getScrollElement: () => scrollRef.current,
    estimateSize: () => ROW_HEIGHT,
    overscan: OVERSCAN,
  });

  const virtualItems = virtualizer.getVirtualItems();

  // Padding rows replace the non-rendered rows above and below the viewport
  const paddingTop = virtualItems.length > 0 ? virtualItems[0].start : 0;
  const paddingBottom = virtualItems.length > 0
    ? virtualizer.getTotalSize() - virtualItems[virtualItems.length - 1].end
    : 0;

  // Select All state
  const isAllSelected = filteredRows.length > 0 && selection.length === filteredRows.length;
  const isIndeterminate = selection.length > 0 && !isAllSelected;

  const handleSelectAll = () => {
    if (isAllSelected || isIndeterminate) {
      setSelection([]);
    } else {
      setSelection([...filteredRows]);
    }
  };

  const getRowId = (row) => row.id ?? row._id ?? JSON.stringify(row);

  const isRowSelected = (row) => {
    const id = getRowId(row);
    return selection.some((r) => getRowId(r) === id);
  };

  const handleToggleRow = (row) => {
    const id = getRowId(row);
    if (isRowSelected(row)) {
      setSelection((prev) => prev.filter((r) => getRowId(r) !== id));
    } else {
      setSelection((prev) => [...prev, row]);
    }
  };

  const colSpan = columns.length + (showSelectionColumn ? 1 : 0);

  if (!loading && filteredRows.length === 0 && !showEmptyState) return null;

  const headerCells = columns.map((col) => {
    if (!col.isFilterActive) {
      return (
        <th key={col.accessor} className={styles.th}>
          <span className={styles.thText}>{col.title}</span>
        </th>
      );
    }
    const filterVal = columnFilters[col.accessor];
    const isFiltering = typeof filterVal === 'string'
      ? filterVal.trim() !== ''
      : Array.isArray(filterVal) && filterVal.length > 0;
    return (
      <th key={col.accessor} className={styles.th}>
        <Popover width={260} position='bottom-end' withArrow shadow='md'>
          <Popover.Target>
            <UnstyledButton className={styles.headerButton}>
              <span className={styles.thText}>{col.title}</span>
              <IconFilter
                size={14}
                className={isFiltering ? styles.filterActive : styles.filterIcon}
              />
            </UnstyledButton>
          </Popover.Target>
          <Popover.Dropdown>
            <ColumnFilter
              label={col.title}
              accessor={col.accessor}
              filters={columnFilters}
              setFilters={setColumnFilters}
              options={filterOptions[col.accessor] ?? []}
              type={col.filterType}
              isUrlChange={false}
            />
          </Popover.Dropdown>
        </Popover>
      </th>
    );
  });

  const bodyRows = virtualItems.map((virtualRow) => {
    const row = filteredRows[virtualRow.index];
    const selected = isRowSelected(row);
    return (
      <tr
        key={getRowId(row)}
        style={{ height: ROW_HEIGHT }}
        className={selected ? styles.selectedRow : ''}
      >
        {showSelectionColumn && (
          <td className={`${styles.td} ${styles.checkboxCell}`}>
            <Checkbox
              checked={selected}
              onChange={() => handleToggleRow(row)}
              aria-label={`בחר שורה ${getRowId(row)}`}
            />
          </td>
        )}
        {columns.map((col) => (
          <td key={col.accessor} className={styles.td}>
            <ColumnContent
              accessor={col.accessor}
              item={row}
              actions={col.actions}
              transforms={col.transforms}
              type={col.type}
            />
          </td>
        ))}
      </tr>
    );
  });

  return (
    <div className={styles.wrapper}>
      <div className={styles.container}>
        <LoadingOverlay visible={loading} loaderProps={{ size: 'md' }} />
        <div
          ref={scrollRef}
          style={{ maxHeight: height, minHeight: loading ? 120 : undefined }}
          className={styles.scrollContainer}
        >
          <table className={styles.table}>
            <thead className={styles.thead}>
              <tr>
                {showSelectionColumn && (
                  <th className={`${styles.th} ${styles.checkboxCell}`}>
                    <Checkbox
                      checked={isAllSelected}
                      indeterminate={isIndeterminate}
                      onChange={handleSelectAll}
                      aria-label='בחר הכל'
                    />
                  </th>
                )}
                {headerCells}
              </tr>
            </thead>
            <tbody className={styles.tbody}>
              {paddingTop > 0 && (
                <tr className={styles.paddingRow}>
                  <td style={{ height: paddingTop }} colSpan={colSpan} />
                </tr>
              )}
              {bodyRows}
              {paddingBottom > 0 && (
                <tr className={styles.paddingRow}>
                  <td style={{ height: paddingBottom }} colSpan={colSpan} />
                </tr>
              )}
            </tbody>
          </table>
          {!loading && filteredRows.length === 0 && showEmptyState && <NoData />}
        </div>
      </div>
    </div>
  );
};

VirtualTable.propTypes = {
  columns: PropTypes.array,
  getData: PropTypes.func,
  filterOptions: PropTypes.object,
  columnFilters: PropTypes.object,
  setColumnFilters: PropTypes.func,
  onSelectRow: PropTypes.func,
  showSelectionColumn: PropTypes.bool,
  height: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  showEmptyState: PropTypes.bool,
  ready: PropTypes.bool,
};

export default VirtualTable;
